import React, { useState, useEffect, memo } from "react";
import { Box, Typography, Button, keyframes, Icon } from "@mui/material";
import { useTheme } from "@emotion/react";

// Slide down animation for the notification screen
const slideDown = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animation for the "Rain Delay" appearing
const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Pulse animation for emphasis
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

// Button click animation
const buttonClick = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(0.95); }
  100% { transform: scale(1); }
`;

const CalendarExplainerAnimation = ({ sx }) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [eventDots, setEventDots] = useState([]);
  const theme = useTheme();

  // Animation sequence
  useEffect(() => {
    const sequence = async () => {
      // Step 0: Initial state (button visible)
      setAnimationStep(0);
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Step 1: Button clicked, transition to calendar
      setAnimationStep(1);
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Step 2: Show calendar grid
      setAnimationStep(2);
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Step 3: Add event dots one by one in serial order
      const dotPositions = [
        { row: 0, col: 2 }, // Day 3
        { row: 0, col: 5 }, // Day 6
        { row: 1, col: 1 }, // Day 9
        { row: 1, col: 4 }, // Day 12
        { row: 2, col: 0 }, // Day 15
        { row: 2, col: 3 }, // Day 18
        { row: 3, col: 2 }, // Day 24
      ];

      for (let i = 0; i < dotPositions.length; i++) {
        setEventDots((prev) => [...prev, dotPositions[i]]);
        await new Promise((resolve) => setTimeout(resolve, 450));
      }

      // Step 4: Show notification
      setAnimationStep(3);
      await new Promise((resolve) => setTimeout(resolve, 4500));

      // Reset and restart
      setAnimationStep(0);
      setEventDots([]);
    };

    const interval = setInterval(sequence, 11500);
    sequence(); // Start immediately

    return () => clearInterval(interval);
  }, []);

  const CalendarGrid = memo(() => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: { xs: "4px", sm: "6px", md: "8px" },
        width: "80%",
        maxWidth: { xs: "150px", sm: "175px", md: "200px" },
        height: { xs: "110px", sm: "130px", md: "150px" },
        padding: { xs: "8px", sm: "10px", md: "12px" },
      }}
    >
      {/* Calendar header */}
      {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
        <Box
          key={`${day}-${index}`}
          sx={{
            fontSize: { xs: "6px", sm: "7px", md: "8px" },
            fontWeight: 500,
            color: theme.palette.text.secondary,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {day}
        </Box>
      ))}

      {/* Calendar days */}
      {Array.from({ length: 28 }, (_, i) => {
        const row = Math.floor(i / 7);
        const col = i % 7;
        const hasEvent = eventDots.some(
          (dot) => dot.row === row && dot.col === col
        );

        return (
          <Box
            key={i}
            sx={{
              position: "relative",
              fontSize: { xs: "8px", sm: "9px", md: "11px" },
              color: theme.palette.text.primary,
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: { xs: "15px", sm: "17px", md: "20px" },
              width: { xs: "15px", sm: "17px", md: "20px" },
            }}
          >
            {i + 1}
            {hasEvent && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: "-2px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "4px",
                  height: "4px",
                  backgroundColor: theme.palette.overlay.black70,
                  borderRadius: "50%",
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  ));

  const AddButton = memo(() => (
    <Button
      variant="contained"
      size="shrinked"
      sx={{
        textTransform: "none",
        animation: animationStep === 1 ? `${buttonClick} 0.3s ease` : "none",
      }}
    >
      ADD TO CALENDAR
    </Button>
  ));

  const NotificationScreen = memo(() => (
    <Box
      sx={{
        position: "relative",
        width: "200px",
        height: "150px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        padding: "16px",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Notification Card */}
      <Box
        sx={{
          width: "95%",
          backgroundColor: theme.palette.overlay.white95,
          borderRadius: "12px",
          padding: "16px",
          boxShadow: theme.shadows.notification(theme.palette),
          backdropFilter: "blur(20px)",
          animation: `${slideDown} 0.8s ease-out`,
          border: `1px solid ${theme.palette.overlay.white20}`,
        }}
      >
        {/* Notification Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            mb: "12px",
          }}
        >
          {/* Calendar icon */}
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              fontSize: "16px",
              color: theme.palette.overlay.black60,
              opacity: 0.8,
              fontWeight: 300,
            }}
          >
            calendar_month
          </Icon>

          {/* Title placeholder bar */}
          <Box
            sx={{
              flex: 1,
              height: "8px",
              marginTop: "1px",
              backgroundColor: theme.palette.overlay.black20,
              borderRadius: "5px",
            }}
          />

          {/* Right-aligned "now" */}
          <Typography
            sx={{
              fontSize: "10px",
              color: theme.palette.overlay.black70,
              lineHeight: 1,
              fontWeight: 400,
            }}
          >
            now
          </Typography>
        </Box>

        {/* Rain Delay Alert */}
        <Box
          sx={{
            backgroundColor: theme.palette.overlay.black10,
            borderRadius: "6px",
            padding: "6px 8px",
            animation: `${fadeInUp} 1s ease-out 0.5s both, ${pulse} 2s ease-in-out 1.5s infinite`,
            backdropFilter: "blur(10px)",
            border: `1px solid ${theme.palette.overlay.black20}`,
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: theme.palette.overlay.black80,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                fontSize: "14px",
                fontWeight: 300,
                color: theme.palette.overlay.black60,
              }}
            >
              warning
            </Icon>
            Postponed
          </Typography>
        </Box>
      </Box>
    </Box>
  ));

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: "150px", sm: "175px", md: "200px" },
        my: { xs: "1.5%", sm: "2%", md: 2 },
        borderRadius: "12px",
        backgroundColor: theme.palette.smokyWhite,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        transition: "all 0.6s ease-in-out",
        ...sx,
      }}
    >
      {/* Vertical lines - only show during calendar step */}
      {animationStep === 2 && (
        <>
          {/* Left vertical line */}
          <Box
            sx={{
              position: "absolute",
              left: "10%",
              top: 0,
              bottom: 0,
              width: "1px",
              backgroundColor: theme.palette.overlay.black15,
            }}
          />

          {/* Right vertical line */}
          <Box
            sx={{
              position: "absolute",
              right: "10%",
              top: 0,
              bottom: 0,
              width: "1px",
              backgroundColor: theme.palette.overlay.black15,
            }}
          />
        </>
      )}

      {/* Animation Content */}
      {(animationStep === 0 || animationStep === 1) && <AddButton />}

      {animationStep === 2 && <CalendarGrid />}

      {animationStep === 3 && <NotificationScreen />}
    </Box>
  );
};

export default CalendarExplainerAnimation;
